import React, {useState, useEffect, useCallback, useRef} from 'react';
import FilterLayout from '../FilterLayout';
import SMCExpansionPanel from '../../../components/Simple/SMCExpansionPanel';
import {useTranslation} from 'react-i18next';
import RowLayout from '../../../components/Layouts/RowLayout';
import SMCDayPickerInput from '../../../components/Simple/SMCDayPickerInput';
import SMCIcon from '../../../components/Simple/SMCIcon';
import {AdministratorFilterSchema} from '../../../api/DBSchema/Administrator';
import {SMCOption, SMCSelect} from '../../../components/Simple/SMCSelect';

function DateRangeChooser({
    fromDate,
    toDate,
    setFromDate,
    setToDate
}) {
    return (
        <>
        <RowLayout spacing="4px">
            <span style={{ width: 50 }}>From:</span>
            <SMCDayPickerInput
                placeholder="From..."
                value={fromDate}
                onChange={setFromDate}
                maxDate={toDate}
                width={85}
                noFuture={true}
            />
            <SMCIcon icon="clear" onClick={() => setFromDate(null)} />
        </RowLayout>
        <RowLayout spacing="4px">
            <span style={{ width: 50 }}>To:</span>
            <SMCDayPickerInput
                placeholder="To..."
                value={toDate}
                onChange={setToDate}
                minDate={fromDate}
                width={85}
                noFuture={true}
            />
            <SMCIcon icon="clear" onClick={() => setToDate(null)} />
        </RowLayout>
        </>
    );
}

function AdministratorFilterPane({
    initialFilter,
    onFilterChange,
    open
}) {
    const { t } = useTranslation();
    const didMountRef = useRef(true);
    const freeTextSearchRef = useRef();

    useEffect(() => {
        if (open && freeTextSearchRef.current) {
            freeTextSearchRef.current.focus();
        }
    }, [open]);

    const [freeTextSearch, setFreeTextSearch] = useState(
        initialFilter.freeTextSearch ? initialFilter.freeTextSearch.value : ''
    );

    const [role, setRole] = useState(
        initialFilter.role ? initialFilter.role.value : ''
    );

    const [creationFromDate, setCreationFromDate] = useState(null);
    const [creationToDate, setCreationToDate] = useState(null);

    const [modificationFromDate, setModificationFromDate] = useState(null);
    const [modificationToDate, setModificationToDate] = useState(null);

    const resetToDefaults = useCallback(() => {
        setFreeTextSearch('');
        setRole('');

        setCreationFromDate(null);
        setCreationToDate(null);

        setModificationFromDate(null);
        setModificationToDate(null);
    }, []);

    // Update filter request object on value change
    useEffect(() => {
        // Don't run on first render
        if (didMountRef.current === true) {
            didMountRef.current = false;
            return;
        }

        const filterRequestObj = {};
        // Free text search
        if (freeTextSearch) {
            filterRequestObj[AdministratorFilterSchema.freeTextSearch] = { value: freeTextSearch };
        }

        // Role
        if (role) {
            filterRequestObj[AdministratorFilterSchema.role] = { value: role };
        }

        // Creation date range
        if (creationFromDate || creationToDate) {
            filterRequestObj[AdministratorFilterSchema.creationDate] = {
                value: {
                    from: creationFromDate ? creationFromDate.getTime() : null,
                    to: creationToDate ? creationToDate.getTime() : null
                }
            };
        }
        // Modification date range
        if (modificationFromDate || modificationToDate) {
            filterRequestObj[AdministratorFilterSchema.modificationDate] = {
                value: {
                    from: modificationFromDate ? modificationFromDate.getTime() : null,
                    to: modificationToDate ? modificationToDate.getTime() : null
                }
            };
        }

        onFilterChange(filterRequestObj);
    }, [
        onFilterChange,
        freeTextSearch, role,
        creationFromDate, creationToDate,
        modificationFromDate, modificationToDate
    ]);

    return (
        <FilterLayout clearAllAction={resetToDefaults}>
            <SMCExpansionPanel title={t('SMP.ADMINISTRATOR.ROLE')}>
                <SMCSelect width="180px" value={role} onChange={e => setRole(e.target.value)}>
                    <SMCOption value="">{t('SMC_GEN_ANY')}</SMCOption>
                    <SMCOption value={AdministratorFilterSchema.roleValues.administrator}>{t('SMC_ADMIN_ROLE_Administrator')}</SMCOption>
                    <SMCOption value={AdministratorFilterSchema.roleValues.readOnly}>{t('SMC_ADMIN_ROLE_ReadOnly')}</SMCOption>
                    <SMCOption value={AdministratorFilterSchema.roleValues.multiDomain}>{t('SMC_ADMIN_ROLE_MultiDomain')}</SMCOption>
                    <SMCOption value={AdministratorFilterSchema.roleValues.multiDomainReadOnly}>{t('SMC_ADMIN_ROLE_MultiDomainReadOnly')}</SMCOption>
                </SMCSelect>
            </SMCExpansionPanel>
            {/* Creation tab */}
            <SMCExpansionPanel title={t('SMC_OBJECT_CREATETIME')}>
                <DateRangeChooser
                    fromDate={creationFromDate}
                    toDate={creationToDate}
                    setFromDate={setCreationFromDate}
                    setToDate={setCreationToDate}
                />
            </SMCExpansionPanel>
            {/* Modified tab */}
            <SMCExpansionPanel title={t('SMC_OBJECT_MODIFYTIME')}>
                <DateRangeChooser
                    fromDate={modificationFromDate}
                    toDate={modificationToDate}
                    setFromDate={setModificationFromDate}
                    setToDate={setModificationToDate}
                />
            </SMCExpansionPanel>
        </FilterLayout>
    );
}

export default React.memo(AdministratorFilterPane);